import { ApolloQueryResult } from '@apollo/client/core/types';
import { HostingType } from '@atlassian/cssxp-ccp-types';
import { BillingDoc, BillingDocLineItem, BillingDocWithLineItems } from '@atlassian/cssxp-types-billing';
import { Organization } from '@atlassian/cssxp-types-organization';
import {
  ATLASSIAN_ACCESS_PATH,
  BITBUCKET_PATH,
  CCP_ENTITLEMENT_PATH_2,
  CLOUD_SITE_PATH,
  CLOUD_SITE_PATH_2,
  NOT_FOUND_PATH,
  SERVER_LICENSE_PATH,
  SERVICE_LICENSE_PATH,
  SourceSystem,
  STATUSPAGE_PATH_2,
} from '@atlassian/cssxp-typescommon';
import { UnclaimedSiteLabel } from '@atlassian/cssxp-utils-entitlement';
import {
  isAtlassianAccess,
  isBitbucketCloud,
  isCloudParent,
  isConfluenceProduct,
  isJiraProduct,
  isNpfProduct,
  isOrganisationParent,
  isStatuspage,
  SERVICE_BASE_PRODUCTS,
} from '@atlassian/cssxp-utils-product';
import * as React from 'react';
import { BreadcrumbItem } from '../../breadcrumbs/src/breadcrumbs';

const getDefaultHeader = (billingDoc: BillingDoc): React.ReactNode => billingDoc.id;

const getDefaultSubheader = (_billingDoc: BillingDoc): React.ReactNode => '';

const getDefaultIcon = (_billingDoc: BillingDoc): JSX.Element => <div />;

const getDefaultActions = (
  _billingDoc: BillingDoc,
  _onComplete: () => Promise<ApolloQueryResult<unknown>>
): React.ReactNode => undefined;

const getEntitlementRouteFromLineItems = (lineItem: BillingDocLineItem) => {
  const {
    cloudSiteId,
    platform,
    productId,
    productKey,
    baseProduct,
    businessKey,
    sen,
    parentEntitlementKey,
    entitlementNumber,
    entitlement,
  } = lineItem;

  // Converge Billing Docs to similar shape
  const product = productId ?? productKey ?? entitlement?.productId ?? '';
  const id = businessKey ?? sen ?? entitlement?.id ?? entitlementNumber;
  const sourceSystem = entitlement?.sourceSystem ?? SourceSystem.HAMS;
  if (!product) {
    return NOT_FOUND_PATH;
  }

  const parentKey = entitlement?.parentSen ?? parentEntitlementKey;

  // Copied from getEntitlementRoute, eventually converge
  if (SERVICE_BASE_PRODUCTS.includes(`${baseProduct}`)) {
    return `${SERVICE_LICENSE_PATH}/${id}`;
  } else if (`${platform}`.toUpperCase() !== HostingType.CLOUD) {
    return `${SERVER_LICENSE_PATH}/${id}`;
  } else if (isOrganisationParent(product) || isAtlassianAccess(product)) {
    return `${ATLASSIAN_ACCESS_PATH}/${cloudSiteId}`;
  } else if (isBitbucketCloud(product)) {
    return `${BITBUCKET_PATH}/${cloudSiteId ?? id}`;
  } else if (isStatuspage(product)) {
    return `${STATUSPAGE_PATH_2}/${id}`;
  } else if (isNpfProduct(product) || isJiraProduct(product) || isConfluenceProduct(product)) {
    return `${CCP_ENTITLEMENT_PATH_2}/${entitlementNumber ?? id}`;
  } else if (isCloudParent(product) || parentKey) {
    return `${sourceSystem?.toUpperCase() === SourceSystem.CCP ? CLOUD_SITE_PATH_2 : CLOUD_SITE_PATH}/${
      cloudSiteId ?? id
    }`;
  } else {
    return `${CCP_ENTITLEMENT_PATH_2}/${id}`;
  }
};

const getEntitlementNameFromLineItems = (lineItem: BillingDocLineItem, sourceSystem?: SourceSystem) => {
  // New quotes do not have an associated entitlement / SEN key
  if (!lineItem.businessKey && !lineItem.sen) {
    return undefined;
  }

  if (sourceSystem === SourceSystem.CCP) {
    return `${lineItem.cloudSiteHostname ? `${lineItem.cloudSiteHostname}, ` : ''}${
      lineItem.entitlementNumber ?? lineItem.entitlementId
    }`;
  }
  if (`${lineItem.platform}`.toUpperCase() === HostingType.CLOUD) {
    const cloudSiteHostname = lineItem.cloudSiteHostname ?? UnclaimedSiteLabel;

    return `${cloudSiteHostname}, SEN-${lineItem.businessKey ?? lineItem.sen}`;
  }
  return `SEN-${lineItem.businessKey}`;
};

const getDefaultBreadcrumbTrail = (
  _billingDoc: BillingDocWithLineItems,
  _organization?: Organization
): BreadcrumbItem[] => [];

export {
  getDefaultActions,
  getDefaultIcon,
  getDefaultHeader,
  getDefaultSubheader,
  getDefaultBreadcrumbTrail,
  getEntitlementRouteFromLineItems,
  getEntitlementNameFromLineItems,
};
